/**
 * Карта v3 — модель сцены.
 *
 * Сцена собирается из двух источников: topology (что есть в системе) и
 * items (как это разложено на карте v3). Если раскладки нет — подхватываем
 * legacy: позиции из map_layouts и изгибы из connections.waypoints.
 * Читаем legacy, но никогда туда не пишем: эти поля общие с картами v1/v2,
 * и у них другие конвенции координат.
 */
import { NODE_W, NODE_H, PAD, PAD_TOP } from './const';
import { unionRect, naturalCompare } from './math';

let uid = 1;
export const nextId = () => uid++;

export function createScene() {
  return {
    nodes: new Map(),        // devId -> Node
    conts: new Map(),        // locId -> Container
    edges: new Map(),        // connId -> Edge
    order: [],               // порядок контейнеров по глубине (родитель раньше)
    types: new Map(),        // deviceTypeId -> {name, image}
    ctypes: new Map(),       // connectionTypeId -> стиль линии
    version: 0,
    docVersion: 1,
    sceneId: 1,
    dropped: [],             // что отброшено валидатором — покажем честно
  };
}

const num = (v, d = 0) => (typeof v === 'number' && isFinite(v) ? v : d);

/** Глубина локации в дереве — нужна и для z-порядка, и для раскладки. */
function depthOf(locId, byId, guard = 0) {
  const l = byId.get(locId);
  if (!l || guard > 24) return 0;
  return l.parent_id ? 1 + depthOf(l.parent_id, byId, guard + 1) : 0;
}

/**
 * Разворачивает legacy-изгибы карты v2 в наши via.
 * v2 хранит {d, w}: d — доля вдоль линии, w — поперечное смещение.
 * Голый {x,y} (ранняя версия) берём как абсолютную точку.
 */
function importWaypoints(wps, ax, ay, bx, by) {
  if (!Array.isArray(wps) || !wps.length) return [];
  const out = [];
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  for (const p of wps) {
    if (!p || typeof p !== 'object') continue;
    if (typeof p.x === 'number' && typeof p.y === 'number') {
      out.push({ kind: 'point', x: p.x, y: p.y, anchor: { mode: 'world' } });
    } else if (typeof p.d === 'number') {
      const t = Math.max(0, Math.min(1, p.d));
      const w = num(p.w, 0);
      out.push({
        kind: 'point',
        x: ax + dx * t + nx * w,
        y: ay + dy * t + ny * w,
        anchor: { mode: 'world' },
      });
    }
  }
  return out;
}

/**
 * Сборка сцены.
 * @param topology ответ /api/map3/state → topology
 * @param items    сохранённая раскладка v3
 */
export function buildScene(topology, items) {
  const s = createScene();
  const T = topology || {};
  const locs = T.locations || [];
  const devs = T.devices || [];
  const conns = T.connections || [];

  for (const t of T.device_types || []) {
    s.types.set(t.id, { name: t.name, image: t.image_url || t.image || null });
  }
  for (const ct of T.connection_types || []) {
    s.ctypes.set(ct.id, {
      name: ct.name,
      color: ct.color || '#64748b',
      style: ct.line_style || 'solid',
      width: Math.max(1, num(ct.line_width, 2)),
      direction: ct.direction || 'bidirectional',
    });
  }

  // --- сохранённая раскладка v3 ---
  const saved = { device: new Map(), location: new Map(), edge: new Map(), meta: null };
  for (const it of items || []) {
    if (it.kind === 'meta') saved.meta = it.data || {};
    else if (saved[it.kind]) saved[it.kind].set(it.ref_id, it.data || {});
  }

  // --- legacy-позиции из map_layouts (ручная работа владельца, терять нельзя) ---
  const legacy = { device: new Map(), location: new Map() };
  for (const L of T.layouts || []) {
    if (!L || !L.entity_type) continue;
    const bag = legacy[L.entity_type];
    if (bag) bag.set(L.entity_id, L);
  }

  // --- контейнеры локаций ---
  const locById = new Map(locs.map((l) => [l.id, l]));
  for (const l of locs) {
    const sv = saved.location.get(l.id);
    const lg = legacy.location.get(l.id);
    s.conts.set(l.id, {
      id: l.id,
      kind: 'cont',
      locId: l.id,
      parentId: l.parent_id || null,
      depth: depthOf(l.id, locById),
      name: l.name || 'Локация',
      x: num(sv?.x, num(lg?.x, 0)),
      y: num(sv?.y, num(lg?.y, 0)),
      w: Math.max(160, num(sv?.w, num(lg?.width, 320))),
      h: Math.max(120, num(sv?.h, num(lg?.height, 220))),
      autoSize: sv ? sv.autoSize !== false : true,
      collapsed: !!sv?.collapsed,
      pinned: !!sv?.pinned || !!lg,
      border: l.border_color || '#64748b',
      fill: l.fill_color || '#ffffff',
      opacity: num(l.opacity, 18),
      placed: !!(sv || lg),
    });
  }
  s.order = [...s.conts.values()].sort((a, b) => a.depth - b.depth).map((c) => c.id);

  // --- устройства ---
  for (const d of devs) {
    const sv = saved.device.get(d.id);
    const lg = legacy.device.get(d.id);
    s.nodes.set(d.id, {
      id: d.id,
      kind: 'node',
      devId: d.id,
      name: d.name || `#${d.id}`,
      typeId: d.device_type_id,
      typeName: d.device_type_name || '',
      locId: d.location_id || null,
      ip: d.primary_ip || (d.ip_addresses || [])[0] || '',
      imagePath: d.image_path || null,          // своя картинка устройства
      typeImage: d.device_type_image || null,   // картинка его типа
      x: num(sv?.x, num(lg?.x, 0)),
      y: num(sv?.y, num(lg?.y, 0)),
      w: NODE_W,
      h: NODE_H,
      pinned: !!sv?.pinned || !!lg,
      hidden: !!sv?.hidden,
      placed: !!(sv || lg),
      ports: [],
    });
  }

  // --- связи ---
  for (const c of conns) {
    const a = s.nodes.get(c.source_device_id);
    const b = s.nodes.get(c.target_device_id);
    if (!a || !b) { s.dropped.push(`связь #${c.id}: устройство отсутствует`); continue; }

    const sv = saved.edge.get(c.id);
    const st = s.ctypes.get(c.connection_type_id) || {};
    let vias = [];
    if (sv && Array.isArray(sv.vias)) {
      vias = sv.vias
        .filter((v) => v && (v.kind === 'line' || v.kind === 'point'))
        .map((v) => ({
          kind: v.kind,
          axis: v.axis === 'y' ? 'y' : 'x',
          v: num(v.v, 0),
          x: num(v.x, 0),
          y: num(v.y, 0),
          order: num(v.order, 0),
          anchor: v.anchor && v.anchor.mode ? v.anchor : { mode: 'world' },
        }));
    } else if (!sv) {
      // первый заход — подхватим изгибы, нарисованные в карте v2
      vias = importWaypoints(
        c.waypoints,
        a.x + a.w / 2, a.y + a.h / 2,
        b.x + b.w / 2, b.y + b.h / 2,
      );
    }

    s.edges.set(c.id, {
      id: c.id,
      kind: 'edge',
      connId: c.id,
      a: c.source_device_id,
      b: c.target_device_id,
      aIface: c.source_interface || '',
      bIface: c.target_interface || '',
      typeId: c.connection_type_id,
      label: c.label || c.type_name || '',
      color: c.color || st.color || '#64748b',
      style: c.line_style || st.style || 'solid',
      width: Math.max(1, num(c.line_width, st.width || 2)),
      direction: c.direction || st.direction || 'bidirectional',
      subnet: c.subnet_name || '',
      vlan: c.vlan || '',
      mode: sv?.mode || (vias.length ? 'guided' : 'auto'),
      curve: sv?.curve || 'ortho',
      vias,
      aPort: sv?.aPort || { mode: 'auto' },
      bPort: sv?.bPort || { mode: 'auto' },
      labelCfg: sv?.label || { hidden: false, dx: 0, dy: 0 },
      route: null,
      dirty: true,
    });
  }

  s.meta = saved.meta || {};
  assignPortSeats(s);
  return s;
}

/**
 * Сколько связей у каждого устройства и в каком порядке их разводить.
 * Порядок фиксируем по имени интерфейса (натуральная сортировка — eth2 перед eth10),
 * а при его отсутствии по id связи. Стабильный порядок важен: иначе кабели
 * будут прыгать по корпусу при каждой перетрассировке.
 */
export function assignPortSeats(s) {
  const bag = new Map();
  for (const e of s.edges.values()) {
    if (!bag.has(e.a)) bag.set(e.a, []);
    if (!bag.has(e.b)) bag.set(e.b, []);
    bag.get(e.a).push({ edge: e, end: 'a', iface: e.aIface });
    bag.get(e.b).push({ edge: e, end: 'b', iface: e.bIface });
  }
  for (const [devId, list] of bag) {
    list.sort((p, q) => {
      if (p.iface && q.iface) return naturalCompare(p.iface, q.iface);
      if (p.iface) return -1;
      if (q.iface) return 1;
      return p.edge.id - q.edge.id;
    });
    const n = s.nodes.get(devId);
    if (n) n.degree = list.length;
    list.forEach((p, i) => { p.edge[p.end === 'a' ? 'aSeat' : 'bSeat'] = i; });
  }
}

/** Прямоугольники, в которые обязаны помещаться дети локации. */
export function innerBox(c) {
  return {
    x: c.x + PAD,
    y: c.y + PAD_TOP,
    w: Math.max(0, c.w - PAD * 2),
    h: Math.max(0, c.h - PAD_TOP - PAD),
  };
}

export function childrenOf(s, locId) {
  const nodes = [...s.nodes.values()].filter((n) => n.locId === locId && !n.hidden);
  const conts = [...s.conts.values()].filter((c) => c.parentId === locId);
  return { nodes, conts };
}

export function sceneBounds(s) {
  const rects = [];
  for (const c of s.conts.values()) rects.push(c);
  for (const n of s.nodes.values()) if (!n.hidden) rects.push(n);
  return unionRect(rects) || { x: 0, y: 0, w: 800, h: 600 };
}

/**
 * Что уходит на сервер.
 *
 * Возвращаем не только то, что надо записать, но и то, что надо УДАЛИТЬ.
 * Иначе получается ловушка: пользователь согнул линию (строка появилась),
 * потом нажал «Выпрямить» — состояние вернулось к значению по умолчанию,
 * элемент перестал попадать в тело запроса, а сервер умеет только upsert,
 * и старая запись с изгибом остаётся жить. После перезагрузки изгиб
 * возвращается, будто его и не убирали.
 */
export function serialize(s) {
  const items = [];
  const remove = [];

  for (const n of s.nodes.values()) {
    if (!n.placed && !n.pinned && !n.hidden) continue;
    items.push({ kind: 'device', ref_id: n.devId, data: { x: n.x, y: n.y, pinned: !!n.pinned, hidden: !!n.hidden } });
  }
  for (const c of s.conts.values()) {
    items.push({
      kind: 'location', ref_id: c.locId,
      data: { x: c.x, y: c.y, w: c.w, h: c.h, autoSize: !!c.autoSize, collapsed: !!c.collapsed, pinned: !!c.pinned },
    });
  }
  for (const e of s.edges.values()) {
    const has = e.vias.length || e.mode !== 'auto' || e.aPort.mode !== 'auto' ||
                e.bPort.mode !== 'auto' || e.curve !== 'ortho' || e.labelCfg?.hidden;
    if (!has) {
      remove.push({ kind: 'edge', ref_id: e.connId });
      continue;
    }
    items.push({
      kind: 'edge', ref_id: e.connId,
      data: {
        v: 1, mode: e.mode, curve: e.curve, vias: e.vias,
        aPort: e.aPort, bPort: e.bPort, label: e.labelCfg,
      },
    });
  }
  return { items, remove };
}

export function serializeMeta(s, vp, opts) {
  return {
    kind: 'meta', ref_id: 0,
    data: {
      v: 1,
      pan: { x: vp.x, y: vp.y }, zoom: vp.scale,
      grid: !!opts.grid, theme: opts.theme, labels: opts.labels,
      showPorts: !!opts.showPorts,
    },
  };
}
